import { useState, useEffect, useCallback } from "react";
import { Box, Grid, IconButton, Typography } from "@mui/material";
import { sanitize } from "dompurify";
import { AnswerRequired, CODE_KEY, QuestionType } from "@fhc/shared/constants";
import SurveyLinkGenerator from "../components/Questions/SurveyLinkGenerator";
import appApi from "../api";
import Toast from "../components/Toast";
import Button from "../components/UserInput/Buttons/Button";
import AppLayout from "../layouts/AppLayout";
import SurveySingleSelectChoices from "../components/Survey/Choices/SurveySingleSelectChoices";
import SurveyMultipleChoices from "../components/Survey/Choices/SurveyMultipleChoices";
import SurveyRatingChoices from "../components/Survey/Choices/SurveyRatingChoices";
import SurveyTextboxChoices from "../components/Survey/Choices/SurveyTextboxChoices";
import TableSheetChoices from "../components/Survey/Choices/TableSheetChoices";
import AddQuestion from "../components/Questions/AddQuestion";
import ArrowRightIcon from "../components/Icons/ArrowRightIcon";
import ArrowLeftIcon from "../components/Icons/ArrowLeftIcon";
import UpdateQuestionDialog from "../components/Dialogs/UpdateQuestionDialog";
import DeleteQuestionDialog from "../components/Dialogs/DeleteQuestionDialog";
import useSurvey from "../hooks/useSurvey";
import useSurveyNavigation from "../hooks/useSurveyNavigation";
import { calculateTotalPages } from "../utils";
import Heading from "../components/Typography/Heading";
import { TABLE_QUESTION_ROWS_PER_PAGE } from "../constants";
import SurveyCollectors from "../components/SurveyCollectors";
import Spinner from "shared/components/Spinner";
import BulkLinks from "components/BulkLinks";
import BulkLinkGenerator from "components/Questions/BulkLinkGenerator";
import { downloadFile } from "shared/utils";

const Questions = () => {
    const { questionIndex, navigateQuestion, tableQuestionIndices, navigateTable, initializeTableQuestionIndices } =
        useSurveyNavigation();

    const [surveyTheme, setSurveyTheme] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showUpdate, setShowUpdate] = useState(false);
    const [alertProps, setAlertProps] = useState({ show: false, message: "", type: "success" });

    const [collectors, setCollectors] = useState([]);
    const [bulkLinks, setBulkLinks] = useState([]);

    const getQuestions = useCallback(async () => {
        const response = await appApi.get("/company/questions");

        if (response.data.success) {
            const sortedQuestions = sortQuestionsByOrderNumber(response.data.data); // sort questions in the correct order

            initializeTableQuestionIndices(sortedQuestions);

            return sortedQuestions;
        }
    }, [initializeTableQuestionIndices]);

    // Fetch questions when the component mounts
    const [questions, setQuestions] = useState(null);
    useEffect(() => {
        getQuestions()
            .then(setQuestions)
            .catch((error) => console.error(error));
    }, [getQuestions]);

    const { selections, selectChoice } = useSurvey({ questionIndex, questions });

    useEffect(() => {
        async function getTheme() {
            try {
                const response = await appApi.get("/surveys/theme");

                setSurveyTheme(response.data.theme);
            } catch (error) {
                console.error(error);
            }
        }

        getTheme();
    }, []);

    useEffect(() => {
        async function fetchSurveyCollectors() {
            const response = await appApi.get(`/company/survey-links`, {
                params: {
                    is_archived: false, // Only get links that aren't archived
                },
            });

            setCollectors(response.data);
        }

        fetchSurveyCollectors();
    }, []);

    useEffect(() => {
        appApi
            .get(`company/bulk-upload`)
            .then((response) => {
                setBulkLinks(response.data);
            });
    }, [collectors]);

    function sortQuestionsByOrderNumber(questions) {
        return questions.rows.sort((a, b) => a.order_number - b.order_number);
    }

    function onQuestionAdded(data) {
        setQuestions([...questions, data]);
    }

    const onDeleteQuestion = ({ deletedQuestion, success, message }) => {
        const newQuestions = questions.filter((question, index) => {
            // Need to navigate to the previous question if the last question was deleted
            if (index === questions.length - 1 && deletedQuestion.id === question.id) {
                navigateQuestion(-1);
            }

            return deletedQuestion.id !== question.id;
        });
        setQuestions(newQuestions);

        setAlertProps({
            message,
            type: success ? "success" : "error",
            show: true,
        });
    };

    function nextPage() {
        if (
            questions[questionIndex].type === QuestionType.MATRIX &&
            tableQuestionIndices[questions[questionIndex].id] <
                calculateTotalPages(TABLE_QUESTION_ROWS_PER_PAGE, questions[questionIndex].choices.data.length) - 1
        ) {
            navigateTable(questions[questionIndex].id, 1);
        } else {
            navigateQuestion(1);
        }
    }

    function prevPage() {
        if (
            questions[questionIndex].type === QuestionType.MATRIX &&
            tableQuestionIndices[questions[questionIndex].id] > 0
        ) {
            navigateTable(questions[questionIndex].id, -1);
        } else {
            navigateQuestion(-1);
        }
    }

    function onDownloadBulkLink(id) {
        appApi.get(`company/bulk-upload/download/${id}`, { responseType: "blob" }).then((response) => {
            downloadFile(response.data, "fhc_bulk_upload.csv");
        });
    }

    function onDeleteBulkLink(id) {
        appApi.delete(`company/bulk-upload/${id}`).then(() => {
            setBulkLinks(
                bulkLinks.filter((b) => {
                    return b.id !== id;
                }),
            );
        });
    }

    return (
        <AppLayout>
            <div className="mx-[46px] mb-[36px]">
                <Grid container spacing="40px">
                    <Grid item xs={12} sm={12} md={6.2} xl={6.2}>
                        <Box className="flex bg-white border border-slate-100 rounded-[10px] overflow-hidden">
                            <Box className="flex flex-col gap-8 w-full py-[38px] pl-[38px] pr-[50px]">
                                <SurveyLinkGenerator
                                    onLinkGenerated={(newCollector) => {
                                        setCollectors([...collectors, newCollector]);
                                    }}
                                    onBulkLinksGenerated={(newBulkLink) => {
                                        setBulkLinks([...bulkLinks, newBulkLink]);
                                    }}
                                />

                                <SurveyCollectors collectors={collectors} setCollectors={setCollectors} />
                                <div>
                                    <Heading style={{ marginBottom: "16px" }}>Bulk uploads</Heading>
                                    <div className="mb-5 text-[#455468]">Bulk generate links for individual respondents</div>
                                    <BulkLinkGenerator
                                        onBulkLinksGenerated={(newBulkLink) => {
                                            setBulkLinks([...bulkLinks, newBulkLink]);
                                        }}
                                        collectors={collectors}
                                    />
                                </div>
                                <BulkLinks
                                    bulkLinks={bulkLinks}
                                    setBulkLinks={setBulkLinks}
                                    onDownload={onDownloadBulkLink}
                                    onDelete={onDeleteBulkLink}
                                />
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} sm={12} md={5.8} xl={5.8}>
                        <Box className="flex bg-white border border-slate-100 rounded-[10px] overflow-hidden">
                            {questionIndex !== 0 && (
                                <IconButton className="p-0 ml-[22px] h-fit my-auto" onClick={prevPage} disableRipple>
                                    <ArrowLeftIcon className="text-3xl"></ArrowLeftIcon>
                                </IconButton>
                            )}
                            <Box className="flex items-center justify-between w-full py-[38px] pl-[38px] pr-[50px] overflow-auto">
                                <>
                                    {/* Content container */}
                                    <div className="flex-1">
                                        <Heading className="mb-[37px]">All questions</Heading>

                                        {!questions ? (
                                            <Spinner />
                                        ) : (
                                            <div className="flex flex-col">
                                                <div className="mb-[57px]">
                                                    <Typography
                                                        sx={{ fontSize: 14 }}
                                                        dangerouslySetInnerHTML={{
                                                            __html: sanitize(questions[questionIndex].question),
                                                        }}
                                                    />
                                                    {!!questions[questionIndex].secondary_question && (
                                                        <Typography
                                                            sx={{ fontSize: 14, marginTop: "14px" }}
                                                            dangerouslySetInnerHTML={{
                                                                __html: sanitize(
                                                                    questions[questionIndex].secondary_question,
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                <div className="mb-[45px]">
                                                    {questions[questionIndex].type === QuestionType.SINGLE_RESPONSE ? (
                                                        <SurveySingleSelectChoices
                                                            question={questions[questionIndex]}
                                                            selections={selections}
                                                            selectChoice={selectChoice}
                                                            themeColors={surveyTheme}
                                                        />
                                                    ) : questions[questionIndex].type ===
                                                      QuestionType.MULTIPLE_RESPONSE ? (
                                                        <SurveyMultipleChoices
                                                            question={questions[questionIndex]}
                                                            selections={selections}
                                                            selectChoice={selectChoice}
                                                            themeColors={surveyTheme}
                                                        />
                                                    ) : questions[questionIndex].type === QuestionType.RATING_SCALE ? (
                                                        <SurveyRatingChoices
                                                            question={questions[questionIndex]}
                                                            selections={selections}
                                                            selectChoice={selectChoice}
                                                            themeColors={surveyTheme}
                                                        />
                                                    ) : questions[questionIndex].type === QuestionType.TEXTBOX ? (
                                                        <SurveyTextboxChoices
                                                            question={questions[questionIndex]}
                                                            selections={selections}
                                                            selectChoice={selectChoice}
                                                            themeColors={surveyTheme}
                                                            // This information (input type and placeholder) will need to be in the database in the future
                                                            // However, it's just hardcoded for now to resolve https://pmgint.atlassian.net/browse/APPS-1102.
                                                            // The hardcoding can be replace when doing https://pmgint.atlassian.net/browse/APPS-2461.
                                                            inputType={
                                                                questions[questionIndex].code_keys?.includes(
                                                                    CODE_KEY.year_of_birth,
                                                                )
                                                                    ? "number"
                                                                    : "text"
                                                            }
                                                            placeholder={
                                                                questions[questionIndex].code_keys?.includes(
                                                                    CODE_KEY.year_of_birth,
                                                                )
                                                                    ? "Year of birth"
                                                                    : ""
                                                            }
                                                            integerOnly={questions[questionIndex].code_keys?.includes(
                                                                CODE_KEY.year_of_birth,
                                                            )}
                                                        />
                                                    ) : (
                                                        questions[questionIndex].type === QuestionType.MATRIX && (
                                                            <TableSheetChoices
                                                                themeColors={surveyTheme}
                                                                question={questions[questionIndex]}
                                                                pageIndex={
                                                                    tableQuestionIndices[questions[questionIndex].id]
                                                                }
                                                                rowsPerPage={TABLE_QUESTION_ROWS_PER_PAGE}
                                                                selections={selections}
                                                                selectChoice={selectChoice}
                                                            />
                                                        )
                                                    )}
                                                </div>
                                                <Box
                                                    display="flex"
                                                    justifyContent="flex-start"
                                                    gap="20px"
                                                    sx={{ width: "100%" }}
                                                >
                                                    {questions[questionIndex].type !== QuestionType.MATRIX && // User's aren't allowed to update table/matrix questions (table questions can't be created so we don't want to allow updating them)
                                                        questions[questionIndex].required !== AnswerRequired.REQUIRED && (
                                                            <Button size="xs" handleOnClick={() => setShowUpdate(true)}>
                                                                Update
                                                            </Button>
                                                        )}
                                                    {questions[questionIndex].required !== AnswerRequired.REQUIRED && (
                                                        <Button
                                                            size="xs"
                                                            colorTheme="secondary"
                                                            handleOnClick={() => setShowDeleteDialog(true)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    )}
                                                    <UpdateQuestionDialog
                                                        show={showUpdate}
                                                        question={questions[questionIndex]}
                                                        onQuestionUpdated={(updatedQuestion) => {
                                                            setQuestions((previousQuestions) =>
                                                                previousQuestions.map((question) =>
                                                                    question.id === updatedQuestion.id
                                                                        ? updatedQuestion
                                                                        : question,
                                                                ),
                                                            );
                                                        }}
                                                        onClose={(val) => setShowUpdate(val)}
                                                    />

                                                    <DeleteQuestionDialog
                                                        title="Delete Question"
                                                        content="Are you sure you want to delete this question?"
                                                        question={questions[questionIndex]}
                                                        show={showDeleteDialog}
                                                        onClose={() => setShowDeleteDialog(false)}
                                                        onDeleteCallback={onDeleteQuestion}
                                                    />
                                                </Box>
                                            </div>
                                        )}
                                    </div>
                                    <Toast
                                        show={alertProps.show}
                                        message={alertProps.message}
                                        type={alertProps.type}
                                        handleClose={() =>
                                            setAlertProps((previousShowAlert) => ({
                                                ...previousShowAlert,
                                                show: false,
                                            }))
                                        }
                                    />
                                </>
                            </Box>
                            {questions && questionIndex !== questions.length - 1 && (
                                <IconButton className="p-0 mr-[22px] h-fit my-auto" onClick={nextPage} disableRipple>
                                    <ArrowRightIcon className="text-3xl"></ArrowRightIcon>
                                </IconButton>
                            )}
                        </Box>
                        <Box my={4}>
                            <AddQuestion onQuestionAdded={onQuestionAdded}></AddQuestion>
                        </Box>
                    </Grid>
                </Grid>
            </div>
        </AppLayout>
    );
};

export default Questions;
